import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from './Logo';
import {
  LANGUAGE_OPTIONS,
  normalizeLanguageCode,
} from '../utils/languagePreference';
import './Navbar.css';
import toast from 'react-hot-toast';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [homeClickTick, setHomeClickTick] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn, logout, hasVastuConsultantAccess } = useAuth();
  const { cartItems, toggleCart } = useCart();
  const { language, changeLanguage, t } = useLanguage();
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);
  const isAdminUser = user?.role === 'admin';
  const isVastuConsultantUser = user?.role !== 'admin' && (
    ['consultant', 'vastu_consultant'].includes(user?.role) || hasVastuConsultantAccess
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!homeClickTick) return;

    const scrollHome = () => {
      window.scrollTo({ top: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollHome();
    window.requestAnimationFrame(scrollHome);
    const timer = window.setTimeout(scrollHome, 50);

    return () => window.clearTimeout(timer);
  }, [homeClickTick]);

  const handleLogout = () => {
    logout();
    toast.success("You have been logged out.");
    setShowUserMenu(false);
  };

  const handleHomeClick = (event) => {
    event?.preventDefault?.();
    setIsMenuOpen(false);
    setShowUserMenu(false);
    setHomeClickTick((tick) => tick + 1);

    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const getAvatarUrl = () => {
    if (user?.avatar) return user.avatar;
    const name = user?.fullName || 'User';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=8B4513&color=fff&bold=true`;
  };

  const drawerVariants = {
    hidden: { x: '-100%' },
    visible: { 
      x: 0, 
      transition: { duration: 0.15, ease: 'easeOut' }
    },
    exit: { 
      x: '-100%', 
      transition: { duration: 0.1, ease: 'easeIn' }
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.35 } }}
            exit={{ opacity: 0, transition: { duration: 0.25 } }}
            onClick={() => setIsMenuOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <div className={`navbar-wrapper ${scrolled ? 'scrolled' : ''}`}>
        <nav className="navbar">
          <div className="nav-container">
            {/* 1. HAMBURGER */}
            <button
              className={`hamburger ${isMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className="bar"></span>
              <span className="bar"></span>
              <span className="bar"></span>
            </button>

            {/* 2. LOGO */}
            <div className="nav-logo">
              <Link to="/" className="nav-logo-link" onClick={handleHomeClick}>
                <Logo size="small" />
                <span className="nav-brand-text">Anandmaya</span>
              </Link>
            </div>

            {/* 3. DESKTOP NAVIGATION MENU */}
            <ul className="nav-menu desktop-menu">
              <li><NavLink to="/" className="nav-link" onClick={handleHomeClick}>{t('nav.home')}</NavLink></li>
              <li><NavLink to="/bhakti-samadhan" className="nav-link">{t('nav.bhaktiSamadhan')}</NavLink></li>
              <li><NavLink to="/online-pooja" className="nav-link">{t('nav.onlinePooja')}</NavLink></li>
              <li><NavLink to="/live-darshan" className="nav-link">{t('nav.bhaktiDarshan')}</NavLink></li>
              <li><NavLink to="/bhakti-store" className="nav-link">{t('nav.bhaktiStore')}</NavLink></li>
              <li><NavLink to="/bhakti-yoga" className="nav-link">{t('nav.bhaktiYoga')}</NavLink></li>
              <li><NavLink to="/bhakti-jaapa" className="nav-link">{t('nav.bhaktiJaapa')}</NavLink></li>
              {/* Pointing to /vastu with localization helper */}
              <li><NavLink to="/vastu" className="nav-link">{t('nav.vastu', {}, 'Vastu')}</NavLink></li>
            </ul>

            {/* 4. RIGHT SIDE ACTIONS */}
            <div className="nav-actions">
              <div className="language-selector custom-lang-dropdown desktop-language-selector" ref={langMenuRef}>
                <button 
                  className="lang-trigger-btn" 
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  aria-expanded={showLangMenu}
                >
                  <i className="fas fa-globe"></i>
                  <span className="lang-code-text">{normalizeLanguageCode(language).toUpperCase()}</span>
                  <i className={`fas fa-chevron-down chevron-icon ${showLangMenu ? 'rotate' : ''}`}></i>
                </button>
                
                <AnimatePresence>
                  {showLangMenu && (
                    <motion.div
                      className="lang-dropdown-menu"
                      initial={{ opacity: 0, y: 15, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <button
                          key={option.code}
                          className={`lang-option-btn ${normalizeLanguageCode(language) === option.code ? 'active' : ''}`}
                          onClick={() => {
                            changeLanguage(option.code);
                            setShowLangMenu(false);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button className="nav-cart-btn" onClick={toggleCart} aria-label={t('nav.openShoppingCart')}>
                <i className="fas fa-shopping-cart"></i>
                {cartItemCount > 0 && <span className="nav-cart-count">{cartItemCount}</span>}
              </button>

              {isLoggedIn ? (
                <div className="user-menu-container" ref={userMenuRef}>
                  <button className="user-menu-trigger" onClick={() => setShowUserMenu(!showUserMenu)} aria-expanded={showUserMenu}>
                    <img src={getAvatarUrl()} alt="User avatar" className="user-avatar-small" />
                  </button>
                  <AnimatePresence>
                    {showUserMenu && (
                      <motion.div
                        className="user-menu"
                        initial={{ opacity: 0, y: 15, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <div className="user-info">
                          <p className="user-name">{user?.fullName}</p>
                          <p className="user-email">{user?.email}</p>
                        </div>
                        <Link to="/dashboard/bookings" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                          <i className="fas fa-gopuram"></i> {t('nav.dashboard')}
                        </Link>
                        {isAdminUser && (
                          <Link to="/admin/dashboard" className="user-menu-item admin-link" onClick={() => setShowUserMenu(false)}>
                            <i className="fas fa-user-shield"></i> {t('nav.adminPanel')}
                          </Link>
                        )}
                        {isVastuConsultantUser && (
                          <Link to="/vastu-consultant-desk" className="user-menu-item admin-link" onClick={() => setShowUserMenu(false)}>
                            <i className="fas fa-user-tie"></i> Vastu Consultant Desk
                          </Link>
                        )}
                        <button className="user-menu-item logout" onClick={handleLogout}>
                          <i className="fas fa-sign-out-alt"></i> {t('nav.logout')}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="auth-buttons desktop-only">
                  <Link to="/login" className="auth-btn login-btn">{t('nav.login')}</Link>
                  <Link to="/signup" className="auth-btn signup-btn">{t('nav.signUp')}</Link>
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* 5. MOBILE DRAWER */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="mobile-drawer"
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="mobile-drawer-header">
              <Link to="/" className="nav-logo-link" onClick={handleHomeClick}>
                <Logo size="small" />
                <span className="nav-brand-text">Anandmaya</span>
              </Link>
            </div>
            
            <ul className="mobile-nav-menu">
              <li><NavLink to="/" className="nav-link" onClick={handleHomeClick}><i className="fas fa-home"></i><span>{t('nav.home')}</span></NavLink></li>
              <li><NavLink to="/bhakti-samadhan" className="nav-link" onClick={() => setIsMenuOpen(false)}><i className="fas fa-om"></i><span>{t('nav.bhaktiSamadhan')}</span></NavLink></li>
              <li><NavLink to="/online-pooja" className="nav-link" onClick={() => setIsMenuOpen(false)}><i className="fas fa-gopuram"></i><span>{t('nav.onlinePooja')}</span></NavLink></li>
              <li><NavLink to="/live-darshan" className="nav-link" onClick={() => setIsMenuOpen(false)}><i className="fas fa-eye"></i><span>{t('nav.bhaktiDarshan')}</span></NavLink></li>
              <li><NavLink to="/bhakti-store" className="nav-link" onClick={() => setIsMenuOpen(false)}><i className="fas fa-store"></i><span>{t('nav.bhaktiStore')}</span></NavLink></li>
              <li><NavLink to="/bhakti-yoga" className="nav-link" onClick={() => setIsMenuOpen(false)}><i className="fas fa-book-open"></i><span>{t('nav.bhaktiYoga')}</span></NavLink></li>
              <li><NavLink to="/bhakti-jaapa" className="nav-link" onClick={() => setIsMenuOpen(false)}><i className="fas fa-book-open"></i><span>{t('nav.bhaktiJaapa')}</span></NavLink></li>
              {/* Mobile Drawer link pointing to /vastu */}
              <li><NavLink to="/vastu" className="nav-link" onClick={() => setIsMenuOpen(false)}><i className="fas fa-drafting-compass"></i><span>{t('nav.vastu', {}, 'Vastu')}</span></NavLink></li>
            </ul>

            <div className="mobile-language-selector">
              <label className="mobile-language-label">{t('nav.language')}</label>
              <div className="mobile-lang-options">
                {LANGUAGE_OPTIONS.map((option) => (
                  <button
                    key={option.code}
                    className={`mobile-lang-btn ${normalizeLanguageCode(language) === option.code ? 'active' : ''}`}
                    onClick={() => changeLanguage(option.code)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {!isLoggedIn && (
              <div className="mobile-auth-buttons">
                <Link to="/login" className="auth-btn login-btn" onClick={() => setIsMenuOpen(false)}>{t('nav.login')}</Link>
                <Link to="/signup" className="auth-btn signup-btn" onClick={() => setIsMenuOpen(false)}>{t('nav.signUp')}</Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;